import {Inject, Injectable} from '@angular/core';
import {combineLatest, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {ConsoleSession} from '@webmate-web-core/shared/types/console-session';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {
    ConsoleConfig, ProviderType,
    MacVirtConsoleConfig,
    OpenSTFConsoleConfig,
    VCloudConsoleConfig, ProxmoxConsoleConfig
} from '../../console/interfaces/console-config.interface';


@Injectable()
export class ConsoleFactory {

    constructor(private featureService: FeatureService, private webmateOptions: WebmateOptions) {
    }

    public buildConsoleConfigForSession(consoleSession: ConsoleSession, projectId: ProjectId): Observable<ConsoleConfig> {
        if (consoleSession.providerType === 'VCloudTestCenter') {
            return this.buildVCloudConsoleConfigForSession(consoleSession, projectId);
        } else if (consoleSession.providerType === 'OpenSTF') {
            return this.buildOpenStfConsoleConfigForSession(consoleSession, projectId);
        } else if (consoleSession.providerType === 'macvirt') {
            return this.buildMacVirtConfigForSession(consoleSession, projectId);
        } else if (consoleSession.providerType === 'proxmox') {
            return this.buildProxmoxConfigForSession(consoleSession, projectId);
        }
        throw new Error(`Cannot create Console Config for unknown providerType ${consoleSession.providerType}`);
    }

    private buildOpenStfConsoleConfigForSession(consoleSession: ConsoleSession, projectId: ProjectId): Observable<OpenSTFConsoleConfig> {
        const configObj = {
            deviceName: consoleSession.deviceName,
            sessionId: consoleSession.id,
            keepAliveTime: consoleSession.keepAliveTime,
            activeCapabilities: JSON.stringify(consoleSession.activeCapabilities),
            deviceId: consoleSession.deviceId,
            userId: consoleSession.userId,
            projectId: projectId,
            openSTFDeviceSerialId: consoleSession.consoleProperties.openSTFDeviceSerialId,
            imageSocketUrl: `${this.webmateOptions.websocketUri}/console/${consoleSession.id}/openstf/imagesocket`,
            eventSocketUrl: `${this.webmateOptions.websocketUri}/console/${consoleSession.id}/openstf/eventsocket/socket.io`,
            type: "OpenSTF" as ProviderType,
        };

        if (!configObj.activeCapabilities) {
            configObj.activeCapabilities = "{}";
        }

        return of(configObj);
    }

    private buildVCloudConsoleConfigForSession(consoleSession: ConsoleSession, projectId: ProjectId): Observable<VCloudConsoleConfig> {
        return combineLatest([this.featureService.getFeature('webmate.console.default-keyboard-layout'), this.featureService.getFeature('webmate.console.mac-buttons'), this.featureService.getFeature('webmate.console.windows-buttons')]).pipe(map(
            ([keyboardLayout, macKeyboardButtons, winKeyboardButtons]) => {
                return {
                    deviceId: consoleSession.deviceId,
                    userId: consoleSession.userId,
                    sessionId: consoleSession.id,
                    projectId: projectId,
                    deviceName: consoleSession.deviceName,
                    type: "VCloudTestCenter" as ProviderType,
                    vmId: consoleSession.consoleProperties.vmId,
                    vmx: consoleSession.consoleProperties.vmxPath,
                    host: consoleSession.consoleProperties.host,
                    port: consoleSession.consoleProperties.port,
                    ticket: consoleSession.consoleProperties.ticket,
                    keyboardLayout: keyboardLayout !== undefined && keyboardLayout.isActive ? keyboardLayout.layout : 'en-US',
                    keepAliveTime: consoleSession.keepAliveTime,
                    macKeyboardButtons: (consoleSession.platform.platformType.toLowerCase().startsWith('mac') && macKeyboardButtons !== undefined && macKeyboardButtons.isActive) ? macKeyboardButtons.buttons : [],
                    winKeyboardButtons: (consoleSession.platform.platformType.toLowerCase().startsWith('win') && winKeyboardButtons !== undefined && winKeyboardButtons.isActive) ? winKeyboardButtons.buttons : [],
                    activeCapabilities: JSON.stringify(consoleSession.activeCapabilities),
                }
            }
        ))
    }

    private buildMacVirtConfigForSession(consoleSession: ConsoleSession, projectId: ProjectId): Observable<MacVirtConsoleConfig> {
        const configObj = {
            projectId: projectId,
            deviceId: consoleSession.deviceId,
            userId: consoleSession.userId,
            sessionId: consoleSession.id,
            keepAliveTime: consoleSession.keepAliveTime,
            type: 'macvirt' as ProviderType,
            wsUrl: `${this.webmateOptions.websocketUri}/console/${consoleSession.id}/macvirt/websocket`,
            deviceName: consoleSession.deviceName,
        };

        return of(configObj);
    }

    private buildProxmoxConfigForSession(consoleSession: ConsoleSession, projectId: ProjectId): Observable<ProxmoxConsoleConfig> {
        const configObj = {
            projectId: projectId,
            deviceId: consoleSession.deviceId,
            userId: consoleSession.userId,
            sessionId: consoleSession.id,
            keepAliveTime: consoleSession.keepAliveTime,
            type: 'proxmox' as ProviderType,
            wsUrl: `${this.webmateOptions.websocketUri}/console/${consoleSession.id}/proxmox/websocket`,
            deviceName: consoleSession.deviceName,
            password: consoleSession.consoleProperties.password
        };

        return of(configObj);
    }
}
